import { motion } from "framer-motion";

const CHAPTERS = [
  {
    num: "01",
    slug: "term",
    title: "Term Life",
    kicker: "Affordable, definite, powerful",
    body:
      "Coverage for the years that matter most — while the kids are still home, while the mortgage still weighs, while your income still supports a life. 10, 20 or 30 year terms with fixed premiums.",
    stats: [
      { k: "TYPICAL COST", v: "$28/mo" },
      { k: "TERM LENGTH", v: "10 – 30 yrs" },
    ],
    image:
      "https://images.unsplash.com/photo-1609220136736-443140cffec6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    reverse: false,
  },
  {
    num: "02",
    slug: "whole",
    title: "Whole Life",
    kicker: "Coverage that lives as long as you do",
    body:
      "Permanent protection with guaranteed cash value that grows tax-deferred. A policy that becomes an asset — one you can borrow against, one that never expires.",
    stats: [
      { k: "GUARANTEED", v: "For life" },
      { k: "CASH VALUE", v: "Tax-deferred" },
    ],
    image: null,
    reverse: true,
  },
  {
    num: "03",
    slug: "universal",
    title: "Universal Life",
    kicker: "Flexible protection, engineered for change",
    body:
      "Adjust your coverage and premiums as life shifts. Built for the entrepreneur, the second act, the family in flux. Growth potential paired with the certainty of permanent insurance.",
    stats: [
      { k: "PREMIUMS", v: "Flexible" },
      { k: "GROWTH", v: "Market-linked" },
    ],
    image:
      "https://images.unsplash.com/photo-1568605114967-8130f3a36994?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    reverse: false,
  },
  {
    num: "04",
    slug: "final",
    title: "Final Expense",
    kicker: "The last kindness you'll ever give",
    body:
      "A small, dignified policy that covers funeral, burial, and final debts — so the people who love you can grieve without a calculator in their hand. Simple approval, no medical exam.",
    stats: [
      { k: "COVERAGE", v: "$5k – $50k" },
      { k: "APPROVAL", v: "No exam" },
    ],
    image:
      "https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    reverse: true,
  },
];

function Chapter({ c, index }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center py-24 md:py-32 border-b border-ink/10 ${
        c.reverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
      data-testid={`chapter-${c.slug}`}
    >
      {/* Text side */}
      <div className="lg:col-span-6 lg:col-start-1">
        <div className={c.reverse ? "lg:pl-12" : "lg:pr-12"}>
          <div className="flex items-baseline gap-6 mb-8">
            <span className="font-serif italic text-gold text-7xl md:text-8xl font-light leading-none">
              {c.num}
            </span>
            <span className="text-ink/40 text-[10px] tracking-[0.3em]">CHAPTER</span>
          </div>
          <h3 className="font-serif text-ink text-5xl md:text-6xl lg:text-7xl font-light leading-[0.95] mb-6">
            {c.title}
          </h3>
          <p className="font-serif italic text-terracotta text-xl md:text-2xl mb-8 font-light">
            {c.kicker}
          </p>
          <p className="text-ink/70 text-base md:text-lg leading-relaxed max-w-xl font-light">
            {c.body}
          </p>
          <div className="mt-12 grid grid-cols-2 gap-8 max-w-md">
            {c.stats.map((s) => (
              <div key={s.k} className="border-t border-ink/20 pt-4">
                <div className="text-[10px] tracking-[0.25em] text-ink/50 mb-2">{s.k}</div>
                <div className="font-serif text-2xl text-ink tabular-nums">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Image side */}
      {c.image && (
        <div className="lg:col-span-6">
          <div className="relative overflow-hidden aspect-[4/5] max-w-[520px] mx-auto lg:mx-0 lg:ml-auto group">
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] group-hover:scale-105"
              style={{ backgroundImage: `url(${c.image})` }}
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-ink/10" />
          </div>
        </div>
      )}
      {!c.image && (
        <div className="lg:col-span-6 flex items-center justify-center">
          <div className="max-w-[520px] mx-auto lg:mx-0 lg:ml-auto w-full text-right">
            <div className="font-serif italic text-gold text-[9rem] md:text-[12rem] leading-none opacity-30 select-none">
              {c.num}
            </div>
          </div>
        </div>
      )}
    </motion.article>
  );
}

export default function Manifesto() {
  return (
    <section id="products" className="bg-cream" data-testid="manifesto-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="pt-24 md:pt-40 pb-8 max-w-3xl">
          <div className="text-ink/40 text-[11px] tracking-[0.3em] mb-8 flex items-center gap-4">
            <span className="w-8 h-[1px] bg-ink/30" />
            THE MANIFESTO · FOUR CHAPTERS
          </div>
          <h2 className="font-serif text-ink text-5xl md:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tight">
            Not one policy. <span className="italic text-gold">The right one.</span>
          </h2>
        </div>
        {CHAPTERS.map((c, i) => (
          <Chapter key={c.slug} c={c} index={i} />
        ))}
      </div>
    </section>
  );
}
